import React, { memo, useContext, useEffect, useState } from 'react';

import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { filterActions } from '../../features/dashboard/slices/statusSlice';
import { SnackBarType } from '../../features/layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../features/layout/SnackbarContext';
import { AuthContext } from '../../features/login/AuthWrapper';
import { FetchService } from '../fetch.service';
import { buildApplicationsFetchUrl } from '../helpers/application-grid/url-builder';
import { useColumns } from '../hooks/useColumn';
import { IFilter } from '../interfaces/IFilter';
import { ISortState } from '../interfaces/ISortState';
import { TColumnsResolver } from '../types/TColumnsResolver';
import { ApplicationListGrid } from './ApplicationListGrid';
import MuiPagination from './MuiPagination';

interface IPaginatedGridProps {
  viewName: string;
  columnsResolver?: TColumnsResolver;
  decisionFilter: IFilter[];
  stageFilter: IFilter[];
  lifecycleFilters: IFilter[];
  detailedLifecycleFilters: IFilter[];
  forReviewFilter: IFilter[];
  productFilter: IFilter[];
  userFilter: IFilter[];
  carrierUserFilter: IFilter[] | [];
  searchFilter: string;
  agencySearchFilter?: string;
  fromDate: string;
  toDate: string;
  page: number;
}
// Response type derived from endpoint /applications
interface GridDataResponse {
  items: any[];
  pageCount: number;
  totalItems: number;
  next: string;
  previous: string;
}

const arePaginatedGridPropsEqual = (
  prevProps: IPaginatedGridProps,
  newProps: IPaginatedGridProps,
) => {
  return _.isEqual(prevProps, newProps);
};

/**
 * Note: the use of React.memo here is important as it prevents the pagination
 * component from being re-rendered on each new pagination load.
 */
export const PaginatedGrid = memo(
  ({
    viewName,
    columnsResolver,
    decisionFilter,
    stageFilter,
    lifecycleFilters,
    detailedLifecycleFilters,
    forReviewFilter,
    userFilter,
    carrierUserFilter,
    productFilter,
    fromDate,
    toDate,
    searchFilter,
    agencySearchFilter,
    page,
  }: IPaginatedGridProps) => {
    const auth = useContext(AuthContext);
    const columns = useColumns();
    const [sortState, setSortState] = useState<ISortState>({
      field: auth.prioritizedView ? '' : 'applicationSla',
      order: 'ASC',
    });

    const { openSnack } = useContext(SnackbarContext);

    const queryKey = [
      'gridData',
      viewName,
      page,
      sortState,
      decisionFilter,
      stageFilter,
      lifecycleFilters,
      detailedLifecycleFilters,
      forReviewFilter,
      userFilter,
      carrierUserFilter,
      productFilter,
      fromDate,
      toDate,
      searchFilter,
      agencySearchFilter,
      auth.prioritizedView,
    ];
    const dispatch = useDispatch();

    const { data, isLoading, isError, error } = useQuery<
      GridDataResponse,
      Error
    >({
      queryKey,
      queryFn: async () => {
        const url = buildApplicationsFetchUrl({
          viewName,
          pageNumber: page,
          sortBy: sortState.field,
          sortOrder: sortState.order as 'ASC' | 'DESC' | undefined,
          decisionFilter,
          stageFilter,
          lifecycleFilters,
          detailedLifecycleFilters,
          forReviewFilter,
          userFilter,
          carrierUserFilter,
          productFilter,
          fromDate,
          toDate,
          searchFilter,
          agencySearchFilter,
          prioritizedView: auth.prioritizedView || false,
        });

        return FetchService.getInstance().get<GridDataResponse>(url);
      },
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 300,
      staleTime: 1000 * 300,
    });

    useEffect(() => {
      if (isError && error instanceof Error) {
        openSnack({
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: error.message,
        });
      }
    }, [isError, error]);

    const handlePageChange = (_page: string | number) => {
      dispatch(filterActions.onPageChange({ page: Number(_page) }));
    };

    if (isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: '10px',
            height: '500px',
          }}
        >
          <CircularProgress size={40} color="inherit" />
          <p style={{ fontSize: '20px', fontWeight: '400' }}>
            Loading application data...
          </p>
        </div>
      );
    }

    return (
      <>
        <ApplicationListGrid
          rows={data?.items ?? []}
          columns={columns}
          columnsResolver={columnsResolver}
          sortState={sortState}
          setSortState={setSortState}
        />
        <MuiPagination
          currentPage={page}
          pageCount={data?.pageCount ?? 0}
          onPageChange={handlePageChange}
        />
      </>
    );
  },
  arePaginatedGridPropsEqual,
);
